import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { UserService } from './user.service';
import { CurrencyService } from './currency.service';

/// The in-app half of the country redirect. A person who opens a product their country cannot
/// buy is taken to the "not available" page instead of the product. On a full page load the
/// server does this before anything is drawn (CountryRedirectMiddleware); this is the same
/// decision for a product opened from inside the app, where no request reaches the server.
///
/// The promise, on both sides: no machine is ever sent there - a search engine, an AI crawler,
/// a link preview, a scraper dressed as a browser. It does not rest on recognising crawlers; it
/// rests on acting only on a visitor who has PROVED to be a person. The proof is a real input
/// event - a trusted pointer, touch, key or wheel event - in a browser that is not automated.
/// Crawlers render a page without touching it, so they never produce one; and they fetch every
/// page without cookies, so they never carry the cookie it leaves behind (izz_h), which is what
/// the server asks for. The signed-in admin is never redirected either: they must be able to
/// open everything.
@Injectable({ providedIn: 'root' })
export class CountryGateService
{
    /// The server's list (VisitorClassifier), as far as a browser can see itself.
    private static readonly notAPerson = /bot|crawl|spider|slurp|preview|fetch|scan|monitor|checker|validator|lighthouse|pagespeed|headless|phantom|puppeteer|playwright|selenium|google-|googleother|mediapartners|adsbot|facebookexternalhit|whatsapp|telegram|yandex|baidu|duckduck|semrush|ahrefs|bytespider|chatgpt|claude|perplexity|mistral|gemini|agent/i;

    /// Set while the admin is signed in, so the server leaves their page loads alone too.
    static readonly staffCookie = 'izz_staff';

    /// Set once a real input event has been seen in this browser. The server redirects nobody without it.
    static readonly humanCookie = 'izz_h';

    private humanSeen = false;
    private humanSubject = new ReplaySubject<void>(1);

    /// Fires once, when this browser has proved to be in a person's hands (at once, if it did on an earlier visit).
    readonly human$: Observable<void> = this.humanSubject.asObservable();

    constructor(@Inject(PLATFORM_ID) private platformId: any, private user: UserService, private zone: NgZone)
    {
        if (!isPlatformBrowser(this.platformId)) { return; }

        this.user.onUserInfoChanged$.subscribe(info => this.markStaff(info));

        if (this.looksAutomated()) { return; }

        if (this.readCookie(CountryGateService.humanCookie))
        {
            this.proveHuman();
            return;
        }

        // Outside Angular's zone: these fire constantly and must not start change detection.
        this.zone.runOutsideAngular(() =>
        {
            const events = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel'];
            const onInput = (event: Event) =>
            {
                // A script's own dispatchEvent is not a person. Scrolling is not listened to at
                // all: a crawler scrolls a page by script, and that event is trusted.
                if (!event.isTrusted) { return; }
                events.forEach(name => window.removeEventListener(name, onInput, true));
                this.zone.run(() => this.proveHuman());
            };
            events.forEach(name => window.addEventListener(name, onInput, { capture: true, passive: true } as any));
        });
    }

    /// True for a crawler's or a test tool's browser, as far as it gives itself away.
    looksAutomated(): boolean
    {
        if (!isPlatformBrowser(this.platformId)) { return true; }

        const nav: any = navigator;
        const ua = (nav && nav.userAgent) || '';
        if (ua.length < 20 || CountryGateService.notAPerson.test(ua)) { return true; }
        if (nav.webdriver === true) { return true; }
        return !(nav.language || (nav.languages && nav.languages.length > 0));
    }

    /// A person, proven: not automated, and a real input event has been seen in this browser.
    isPerson(): boolean
    {
        return this.humanSeen && !this.looksAutomated();
    }

    /// The site's one country, always: PrimeHat sells there only and answers every visitor as it (SiteMoney.Country
    /// on the server, the owner's Country and currency page), so the in-app redirect and the card prices go by the
    /// country the server does.
    country(): string
    {
        return CurrencyService.site.country;
    }

    /// Nobody sees the shop as another country on PrimeHat (SiteMoney.Country), the admin included - whatever
    /// a "see the shop as" cookie left from before says.
    viewAs(): string
    {
        return '';
    }

    /// The admin seeing the shop as a visitor from another country: never, on PrimeHat (viewAs).
    previewing(): boolean
    {
        return !!this.viewAs();
    }

    isBlocked(blockedCountryCodes: string, country: string): boolean
    {
        return !!country && !!blockedCountryCodes && blockedCountryCodes.toUpperCase().indexOf(';' + country + ';') >= 0;
    }

    /// The signed-in admin. They are never redirected away from a product.
    isAdmin(): boolean
    {
        return this.user.isNijera();
    }

    /// True when this visitor should see the "not available" page instead of this product. The
    /// admin never is - unless they are seeing the shop as another country, when they are sent
    /// exactly where a visitor from there would be.
    shouldRedirect(product: any): boolean
    {
        // Out of stock at the source, or taken offline by the owner: its page is shown to everyone,
        // wherever they are, saying so - there is nothing to sell, and nowhere better to send them.
        if (!product || product.ProductStatus === CountryGateService.stockOutStatus
            || product.ProductStatus === CountryGateService.offlineStatus) { return false; }
        return this.shouldLeave(this.isBlocked(product.BlockedCountryCodes, this.country()));
    }

    /// ProductStatus.StockOut: taken off the storefront by the stock-out rule, its page still public.
    static readonly stockOutStatus = 6;

    /// ProductStatus.Offline: taken off the storefront by the owner; its page public while the product is active.
    static readonly offlineStatus = 7;

    /// The same decision for a product found blocked some other way - the product page's own
    /// delivery answer, which it shows as "Currently unavailable". The same people are taken:
    /// a proven person, and the admin only while seeing the shop as another country.
    shouldLeave(blocked: boolean): boolean
    {
        if (!blocked || !this.country() || (this.isAdmin() && !this.previewing()) || !this.isPerson()) { return false; }
        return true;
    }

    private proveHuman(): void
    {
        if (this.humanSeen) { return; }
        this.humanSeen = true;
        try
        {
            document.cookie = CountryGateService.humanCookie + '=1; path=/; SameSite=Lax; max-age=2592000';
        }
        catch { /* a browser that refuses cookies is still a person here; the server just never redirects it */ }
        this.humanSubject.next();
    }

    private readCookie(name: string): string
    {
        if (!isPlatformBrowser(this.platformId)) { return ''; }
        try
        {
            const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[1]).trim() : '';
        }
        catch { return ''; }
    }

    private markStaff(info: any): void
    {
        try
        {
            const staff = !!info && info.SID > 0 && info.AccountType === 786;
            document.cookie = CountryGateService.staffCookie + '=' + (staff ? '1' : '') + '; path=/; SameSite=Lax; max-age=' + (staff ? 31536000 : 0);

            // Nobody sees the shop as another country, or in a currency of the owner's picking, on
            // PrimeHat (SiteCountry): the old cookies for them are cleared for everybody, the owner too.
            document.cookie = 'izz_view_as=; path=/; SameSite=Lax; max-age=0';
            document.cookie = 'izz_owner_cur=; path=/; SameSite=Lax; max-age=0';
        }
        catch { /* a browser that refuses cookies simply is not marked */ }
    }
}
